import { FormattedMessage, useIntl } from 'react-intl';

import { FlexColumn, Gap } from 'ui-components/components/flex';
import { MDash } from 'ui-components/components/mdash';

import { FormattedWeight } from '../../../../../components/formatted-weight';
import FormattedCurrency from '../../../../../components/formatted-currency/container';
import ExpanderSection from '../../../tab-section/expander-section/expander-section';
import SectionTitle from '../../../tab-section/title';
import { ReturnDetailSectionId } from '../../../fulfillment-plan/order-details-expanded-section-provider';
import ReadonlyField from '../../../fulfillment/fields/readonly-field';
import { SizeLabel, ConfirmationLabel, InsuranceAmountLabel, InsuranceLabel } from '../../../shared-field-labels/shared-field-labels';
import { useReturnDataContext } from '../../return-data-provider';
import { WeightUnit } from '../../../../../enums/weight-unit';

import { messages } from './messages';
import style from './styles.module.less';

const VALID_WIGHT_UNITS = [WeightUnit.Ounces, WeightUnit.Pounds, WeightUnit.Grams, WeightUnit.Kilograms] as const;

type ValidWeightUnit = (typeof VALID_WIGHT_UNITS)[number];

export function ShipmentRecord() {
  return (
    <ExpanderSection
      ariaLabelMessageDescriptor={messages.sectionAriaLabel}
      contentClassName={style.content}
      headerContent={<SectionTitle messageDescriptor={messages.sectionHeaderTitle} />}
      sectionContent={<Content />}
      sectionId={ReturnDetailSectionId.ShipmentRecord}
    />
  );
}

function Content() {
  const { formatNumber } = useIntl();
  const { requestDetails } = useReturnDataContext();

  const status = requestDetails?.shippingInfo?.status ?? '';
  const shipToLocationName = requestDetails?.shippingInfo?.trackingDetails?.shipTo?.locationName ?? '';
  const weight = requestDetails?.shippingInfo?.weight?.value ?? '';
  const weightUnit = requestDetails?.shippingInfo?.weight?.unit as ValidWeightUnit;
  const length = requestDetails?.shippingInfo?.dimensions?.length ?? '';
  const width = requestDetails?.shippingInfo?.dimensions?.width ?? '';
  const height = requestDetails?.shippingInfo?.dimensions?.height ?? '';
  const dimensionUnit = requestDetails?.shippingInfo?.dimensions?.unit ?? '';
  const labelType = requestDetails?.shippingInfo?.labelType ?? '';
  const packageType = requestDetails?.shippingInfo?.packageName ?? '';
  const confirmation = requestDetails?.shippingInfo?.confirmation ?? '';
  const insuranceProvider = requestDetails?.shippingInfo?.insurance?.insuranceProvider ?? '';
  const insuredAmount = requestDetails?.shippingInfo?.insurance?.insuredValue?.value ?? 0;
  const insuredCurrency = requestDetails?.shippingInfo?.insurance?.insuredValue?.currency ?? null;

  const isInsured = insuranceProvider.toLocaleLowerCase() !== 'none';
  const isSupportedWeightUnit = VALID_WIGHT_UNITS.includes(weightUnit);
  const formattedSize = [length, width, height]
    .filter(Boolean)
    .map((val) => formatNumber(val))
    .join(' x ');

  return (
    <FlexColumn gap={Gap.Space16}>
      <ReadonlyField label={<FormattedMessage {...messages.statusLabel} />}>
        {status || <FormattedMessage {...messages.unknownStatusValue} />}
      </ReadonlyField>
      <ReadonlyField label={<FormattedMessage {...messages.returnToLabel} />}>{shipToLocationName}</ReadonlyField>
      <ReadonlyField label={<FormattedMessage {...messages.weightLabel} />}>
        {isSupportedWeightUnit ? <FormattedWeight weight={{ value: weight, unit: weightUnit }} /> : <MDash />}
      </ReadonlyField>
      <ReadonlyField
        label={dimensionUnit ? <SizeLabel dimensionUnit={dimensionUnit} /> : <FormattedMessage {...messages.dimensionsLabel} />}
      >
        {formattedSize || <MDash />}
      </ReadonlyField>
      <ReadonlyField label={<FormattedMessage {...messages.shippingLabelTypeLabel} />}>
        <FormattedMessage {...messages.shippingLabelTypeValue} values={{ labelType }} />
      </ReadonlyField>
      <ReadonlyField label={<FormattedMessage {...messages.packageTypeLabel} />}>{packageType}</ReadonlyField>
      <ReadonlyField label={ConfirmationLabel}>{confirmation}</ReadonlyField>
      <ReadonlyField label={InsuranceLabel}>{insuranceProvider}</ReadonlyField>
      {isInsured ? (
        <ReadonlyField label={InsuranceAmountLabel}>
          <FormattedCurrency currencyCode={insuredCurrency} value={insuredAmount} />
        </ReadonlyField>
      ) : null}
    </FlexColumn>
  );
}
